import { IMedia } from '@rendora/common/entities/upload_media_v3';
import { UPLOAD_STATUS } from '@rendora/common/types/enums';
import type OSS from 'ali-oss';
import { get } from 'lodash-es';
import type {
  IAuth,
  IBizParams,
  IDataSource,
  IExtras,
  IPrepareSuccess,
  IServiceOptions,
  ISuccess,
  TOnFail,
  TOnProgress,
  TOnSuccess,
} from './types';

// 上传错误类型定义
interface IUploadError {
  code?: string;
  message: string;
  upload_secret_id?: string;
  info?: unknown;
}

interface IAbort {
  status: number;
  name: string;
}

export default class XOSSUpload {
  TAG = '[XOSSUpload]';
  auth!: IAuth;
  client!: OSS;
  cpt = {
    name: '',
    uploadId: '', // AliOSS uploadId，用于取消上传
  };

  callbackConfig = {
    host: '',
    url: '',
  };

  dataSource!: IDataSource;
  eta = 0; // 预计完成时间，用于处理 99%
  file!: File;
  interval = 3000; // 状态轮询间隔，3 秒
  media: IMedia = {} as unknown as IMedia;
  params!: IBizParams;
  progress = {
    current: 0,
    delta: 0,
  };

  ignore_query_status = false;
  is_transcoding = false; // 默认资源不转码
  project_media_group_id = 0;
  timer = 0;
  retryTimer = 0; // startPolling 查询失败时的重试 timer
  ossUploadDone = false; // 上传 oss 是否完成
  uid = '';
  upload_secret_id = '';
  upload_max_percent = 0; // 上传 oss 最大进度值
  /** prepare 接口返回的 upload_full_name，用于上传模拟数据标题 */
  upload_full_name = '';
  pollingStarted = false; // 轮询是否已启动，避免重复启动
  /** 用户主动取消时是否删除服务端数据 */
  private userCancelDeleteData = false;
  /** 已取消，停止所有轮询和异步逻辑 */
  private isCancelled = false;

  // 日志控制
  private debugMode = true;
  private lastLogTime = 0;
  private logThrottle = 1000; // 日志节流间隔，1秒

  // 上传配置常量
  static readonly UPLOAD_CONFIG = {
    SMALL_FILE_THRESHOLD: 1024 * 1024, // 1MB
    PARALLEL_COUNT: 4,
    PART_SIZE: 1024 * 1024,
    QUERY_INTERVAL: 3000,
    MAX_QUERY_PERCENT: 99,
    // 动态轮询间隔配置
    POLLING_INTERVALS: {
      SMALL_FILE: 1000, // 小文件1秒轮询
      MEDIUM_FILE: 2000, // 中等文件2秒轮询
      LARGE_FILE: 3000, // 大文件3秒轮询
    },
    FILE_SIZE_THRESHOLDS: {
      SMALL: 10 * 1024 * 1024, // 10MB
      MEDIUM: 100 * 1024 * 1024, // 100MB
    },
  };

  onProgress: TOnProgress = (percent: number, extras: IExtras) => {
    this.log('debug', '进度更新', percent, extras);
  };

  onOssSuccess = () => {
    this.log('info', 'OSS上传完成');
  };

  onSuccess: TOnSuccess = (media: ISuccess) => {
    this.log('info', '上传完成', media);
  };

  onFail: TOnFail = (msg: string, extras: IExtras) => {
    this.log('error', '上传失败', msg, extras);
  };

  constructor(options: IServiceOptions) {
    const {
      auth,
      dataSource,
      is_transcoding,
      project_media_group_id,
      params,
      onProgress,
      onOssSuccess,
      onSuccess,
      onFail,
    } = options;
    if (!options.auth) {
      this.log('error', '初始化需要: 用户信息 user_id');
      return;
    }
    if (!options.dataSource) {
      this.log('error', '初始化需要：实现 dataSource');
      return;
    }
    this.auth = auth;
    this.dataSource = dataSource;
    if (is_transcoding !== undefined) {
      this.is_transcoding = is_transcoding;
    }
    if (project_media_group_id !== undefined) {
      this.project_media_group_id = project_media_group_id;
    }
    this.params = params;
    if (onProgress) {
      this.onProgress = onProgress;
    }

    if (onSuccess) {
      this.onSuccess = onSuccess;
    }

    if (onOssSuccess) {
      this.onOssSuccess = onOssSuccess;
    }

    if (onFail) {
      this.onFail = onFail;
    }

    this.log('info', '初始化成功');
  }

  getTimeName(name?: string) {
    return `${this.TAG} ${this.upload_secret_id} ${this.uid}${name ? ` ${name}` : ''}`;
  }

  // 智能日志方法
  private log(level: 'info' | 'warn' | 'error' | 'debug', message: string, ...args: unknown[]) {
    const now = Date.now();
    const shouldLog =
      this.debugMode ||
      level === 'error' ||
      level === 'warn' ||
      (level === 'info' && now - this.lastLogTime > this.logThrottle);

    if (shouldLog) {
      const prefix = `${this.getTimeName()} [${level.toUpperCase()}]`;
      switch (level) {
        case 'error':
          console.error(prefix, message, ...args);
          break;
        case 'warn':
          console.warn(prefix, message, ...args);
          break;
        case 'debug':
          if (this.debugMode) {
            console.log(prefix, message, ...args);
          }
          break;
        default:
          console.log(prefix, message, ...args);
          this.lastLogTime = now;
      }
    }
  }

  // 设置调试模式
  setDebugMode(enabled: boolean) {
    this.debugMode = enabled;
  }

  // 根据文件大小动态获取轮询间隔
  getDynamicPollingInterval(): number {
    if (!this.file) {
      return XOSSUpload.UPLOAD_CONFIG.QUERY_INTERVAL;
    }

    const fileSize = this.file.size;
    const { POLLING_INTERVALS, FILE_SIZE_THRESHOLDS } = XOSSUpload.UPLOAD_CONFIG;

    // 根据文件大小和当前进度调整轮询间隔
    let baseInterval: number;
    if (fileSize <= FILE_SIZE_THRESHOLDS.SMALL) {
      baseInterval = POLLING_INTERVALS.SMALL_FILE;
    } else if (fileSize <= FILE_SIZE_THRESHOLDS.MEDIUM) {
      baseInterval = POLLING_INTERVALS.MEDIUM_FILE;
    } else {
      baseInterval = POLLING_INTERVALS.LARGE_FILE;
    }

    // 进度越高，轮询间隔越长，减少不必要的请求
    const progressFactor = this.progress.current > 90 ? 1.5 : 1;
    return Math.round(baseInterval * progressFactor);
  }

  /**
   * 实例化 oss
   */
  async genClient(res: IPrepareSuccess) {
    const OssClient = await import('ali-oss').then(m => m.default);
    return new OssClient({
      accessKeyId: res.credential.access_key_id,
      accessKeySecret: res.credential.access_key_secret,
      bucket: res.credential.bucket,
      region: res.credential.region,
      stsToken: res.credential.security_token,
      refreshSTSToken() {
        return Promise.resolve({
          accessKeyId: res.credential.access_key_id,
          accessKeySecret: res.credential.access_key_secret,
          stsToken: res.credential.security_token,
        });
      },
    });
  }

  /**
   * 生成 oss.callback.body
   */
  genBodyTemplate() {
    let templateString = 'bucket=${bucket}';
    templateString += '&object=${object}';
    templateString += '&etag=${etag}';
    templateString += '&size=${size}';
    templateString += '&mimeType=${mimeType}';
    templateString += '&imageInfo.height=${imageInfo.height}';
    templateString += '&imageInfo.width=${imageInfo.width}';
    templateString += '&imageInfo.format=${imageInfo.format}';
    templateString += '&contentMd5=${contentMd5}';
    templateString += '&clientIp=${clientIp}';
    templateString += '&reqId=${reqId}';
    templateString += '&user_id=${x:user_id}';
    templateString += '&is_transcoding=${x:is_transcoding}';
    templateString += '&project_media_group_id=${x:project_media_group_id}';
    templateString += '&upload_secret_id=${x:upload_secret_id}';
    templateString += '&upload_path=${x:upload_path}';
    templateString += '&real_filename=${x:real_filename}';
    for (const [k, v] of Object.entries(this.params)) {
      if (typeof v === 'string' && v === '') {
        this.log('warn', `${k} is empty`);
      } else {
        templateString += `&${k}=$\{x:${k}}`;
      }
    }
    return templateString;
  }

  onProgressUpdate(p: number) {
    const intPercent = Math.min(Math.max(Math.round(p), 0), 100);

    // 避免重复更新相同进度
    if (intPercent === this.progress.current) {
      return;
    }

    this.log('debug', 'progress:', intPercent);
    this.progress.current = Math.max(intPercent, this.progress.current);
    this.onProgress(intPercent, {
      uid: this.uid,
      file: this.file,
      media: this.media,
      upload_full_name: this.upload_full_name || undefined,
    });
  }

  onFailUpdate(msg: string, _info = {}) {
    this.log('warn', '失败:', msg);
    this.onFail(msg, {
      uid: this.uid,
      file: this.file,
      media: this.media,
      upload_full_name: this.upload_full_name || undefined,
    });
  }

  onSuccessUpdate() {
    console.timeEnd(this.getTimeName('后端耗时'));
    this.log('info', '上传成功完成');
    this.onSuccess({
      uid: this.uid,
      upload_path: this.cpt.name,
      ...this.media,
      file_url: this.media.file_url || this.media.url,
    } as ISuccess);
  }

  async multipartUpload(res: IPrepareSuccess) {
    if (!this.file) {
      return;
    }

    const { host, url } = this.callbackConfig;
    const { user_id } = this.auth;
    const { resource_type, upload_type, model_type, path_prefix, project_id } = this.params;
    const { upload_secret_id, upload_path } = res;
    this.upload_secret_id = upload_secret_id;
    this.cpt.name = upload_path;
    const client = await this.genClient(res);
    this.client = client;
    const body = this.genBodyTemplate();
    const options = {
      // 获取分片上传进度、断点和返回值
      // cpt / res 暂且不用
      progress: (p: number, cpt: { uploadId: string }) => {
        if (p !== 1) {
          // cpt 在上传完成会为 null
          this.cpt.uploadId = cpt.uploadId;
        } else {
          this.onOssSuccess();
          this.ossUploadDone = true;
        }
        this.progress.current = Math.max(this.upload_max_percent * p, this.progress.current);
        this.onProgressUpdate(this.progress.current);
      },
      // 使用配置常量替代硬编码
      parallel:
        this.file.size < XOSSUpload.UPLOAD_CONFIG.SMALL_FILE_THRESHOLD
          ? 1
          : XOSSUpload.UPLOAD_CONFIG.PARALLEL_COUNT,
      partSize: XOSSUpload.UPLOAD_CONFIG.PART_SIZE,
    };
    const customValue = {
      is_transcoding: this.is_transcoding,
      project_media_group_id: this.project_media_group_id,
      user_id,
      resource_type,
      upload_type,
      upload_secret_id,
      upload_path,
      real_filename: this.file.name,
    };
    if ('project_id' in this.params) {
      Object.assign(customValue, { project_id });
    }

    if ('model_type' in this.params) {
      Object.assign(customValue, { model_type });
    }

    if ('path_prefix' in this.params) {
      Object.assign(customValue, { path_prefix });
    }

    try {
      console.time(this.getTimeName('OSS耗时'));
      const ossRes = await client.multipartUpload(upload_path, this.file, {
        ...options,
        callback: {
          host,
          url,
          body,
          customValue,
        },
      });
      const ossResAny = ossRes as unknown as Record<string, unknown>;
      // 兼容两种结构：{ data, media } 嵌套 或 扁平结构（upload_status 等在顶层）
      const data =
        ossResAny?.data != null && typeof ossResAny.data === 'object'
          ? (ossResAny.data as Record<string, unknown>)
          : ossResAny;
      const media = ossResAny?.media as IMedia | undefined;
      console.timeEnd(this.getTimeName('OSS耗时'));
      console.time(this.getTimeName('后端耗时'));
      this.log('debug', 'OSS上传完成，开始后端处理');

      if (!data || typeof data !== 'object' || Object.keys(data).length === 0) {
        this.startPolling();
        return;
      }

      const uploadStatus = data.upload_status as number | undefined;
      if (uploadStatus === UPLOAD_STATUS.FINISHED) {
        this.finish({
          ...(media ?? (data as unknown as IMedia)),
          relatedObject: (ossRes as { related_object?: unknown }).related_object,
        });
        return;
      }

      if (uploadStatus === UPLOAD_STATUS.ERROR) {
        this.backendCancel(8);
        this.onFailUpdate(
          (data.failed_info as { detail?: string })?.detail ?? '上传失败',
          (data.failed_info as object) ?? {},
        );
        return;
      }

      // upload_status 1 (处理中) 或 3 等：继续轮询直到终态
      this.startPolling();
    } catch (err: unknown) {
      this.handleUploadError(err);
    }
  }

  async upload(file: File & { uid: string }) {
    if (!file) {
      return;
    }

    this.uid = file.uid || `uid-${+Date.now()}`;
    this.file = file;
    const { name: filename, size: filesize } = this.file;
    const data = {
      auth_type: 'sts',
      filename,
      filesize,
      ...this.params,
    };
    try {
      const res = await this.dataSource.prepare(data);
      this.callbackConfig.host = res.callback_host;
      this.callbackConfig.url = res.callback_url;
      this.upload_max_percent = res.max_progress_percent;
      this.upload_secret_id = res.upload_secret_id;
      this.upload_full_name = (res as { upload_full_name?: string }).upload_full_name ?? filename;

      // 立即触发0%进度，不依赖OSS返回
      this.onProgressUpdate(0);

      this.startPolling(true);

      this.multipartUpload(res);
    } catch (err: unknown) {
      this.log('error', '上传准备失败', err);
      this.handlePrepareError(err);
    }
  }

  async simpleUpload(file: File & { uid: string }) {
    if (!file) {
      return;
    }
    this.ignore_query_status = true;
    this.uid = file.uid || `uid-${+Date.now()}`;
    this.file = file;
    const { name: filename, size: filesize } = this.file;
    const data = {
      auth_type: 'sts',
      filename,
      filesize,
      ...this.params,
    };
    try {
      const res = await this.dataSource.prepareSimpleUpload(data);
      const { upload_path } = res;
      this.cpt.name = upload_path;
      this.upload_full_name = (res as { upload_full_name?: string }).upload_full_name ?? filename;

      // 立即触发0%进度，不依赖OSS返回
      this.onProgressUpdate(0);

      const client = await this.genClient(res);
      this.client = client;
      const options = {
        // 获取分片上传进度、断点和返回值
        // cpt / res 暂且不用
        progress: (p: number, cpt: { uploadId: string }) => {
          if (p !== 1) {
            // cpt 在上传完成会为 null
            this.cpt.uploadId = cpt.uploadId;
          } else {
            this.ossUploadDone = true;
          }

          this.progress.current = this.upload_max_percent * p;
          this.onProgressUpdate(this.progress.current);
        },
        // 使用配置常量替代硬编码
        parallel:
          this.file.size < XOSSUpload.UPLOAD_CONFIG.SMALL_FILE_THRESHOLD
            ? 1
            : XOSSUpload.UPLOAD_CONFIG.PARALLEL_COUNT,
        partSize: XOSSUpload.UPLOAD_CONFIG.PART_SIZE,
      };
      try {
        console.time(this.getTimeName('OSS耗时'));
        await client.multipartUpload(upload_path, this.file, options);
        console.timeEnd(this.getTimeName('OSS耗时'));
        this.finish(res as unknown as IMedia);
      } catch (err) {
        this.log('error', '简单上传失败', err);
        this.onFailUpdate(JSON.stringify(err));
      }
    } catch (err) {
      this.log('error', '简单上传准备失败', err);
      this.handlePrepareError(err);
    }
  }

  // 统一的状态轮询启动方法
  async startPolling(isEarly = false) {
    // 防止重复启动轮询
    if (this.pollingStarted) {
      this.log('debug', '轮询已启动，跳过重复启动');
      return;
    }
    if (this.isCancelled) return;

    // 立即进行一次状态查询
    const res = await this.queryStatus();
    if (this.isCancelled) return;
    if (!res) {
      // 如果查询失败，延迟重试
      this.retryTimer = window.setTimeout(() => this.startPolling(isEarly), 1000);
      return;
    }

    if (this.ignore_query_status) {
      return;
    }

    // 标记轮询已启动
    this.pollingStarted = true;

    // 使用动态轮询间隔
    this.interval = this.getDynamicPollingInterval();

    // 设置预计完成时间和轮询参数
    this.eta = res.eta * 1000;
    const now = +Date.now();
    const count = Math.ceil((this.eta - now) / this.interval);
    this.progress.delta =
      (XOSSUpload.UPLOAD_CONFIG.MAX_QUERY_PERCENT - this.progress.current) / count;

    this.log(
      'info',
      isEarly ? '提前轮询启动' : '轮询启动', // @xi18n-ignore
      '，轮询间隔:', // @xi18n-ignore
      this.interval,
      'ms，预计轮询次数:', // @xi18n-ignore
      count,
      ',progress 步长:', // @xi18n-ignore
      this.progress.delta,
    );

    // 第一次查询后也触发进度更新
    if (this.progress.current + this.progress.delta <= XOSSUpload.UPLOAD_CONFIG.MAX_QUERY_PERCENT) {
      this.onProgressUpdate(this.progress.current + this.progress.delta);
    }

    this.startQuery();
  }

  startQuery() {
    if (this.isCancelled) return;
    this.timer = window.setTimeout(async () => {
      if (this.isCancelled) return;
      const res = await this.queryStatus();
      if (this.isCancelled) return;
      if (!res) {
        return;
      }

      const now = +Date.now();
      if (now >= this.eta) {
        this.onProgressUpdate(XOSSUpload.UPLOAD_CONFIG.MAX_QUERY_PERCENT);
      } else {
        const newProgress = this.progress.current + this.progress.delta;
        const targetProgress = Math.min(newProgress, XOSSUpload.UPLOAD_CONFIG.MAX_QUERY_PERCENT);
        this.onProgressUpdate(targetProgress);
      }

      // 继续轮询
      this.startQuery();
    }, this.interval);
  }

  queryStatus = async () => {
    const res = await this.dataSource.queryStatus({
      upload_secret_id: this.upload_secret_id,
    });
    if (this.isCancelled) return null;

    this.media.media_type = res.media_type;
    this.media.name = res.filename;
    this.media.size = res.filesize;
    this.media.relatedObject = res.related_object;
    this.media.update_time = res.update_time;
    this.media.create_time = res.create_time;
    this.media.id = res.id;
    this.media.account_id = res.user_id;

    this.onProgressUpdate(res?.percent);

    if (res?.upload_status === UPLOAD_STATUS.FINISHED) {
      this.finish({
        ...res.media,
        relatedObject: res?.related_object,
        project_media_id: res?.project_media_id,
      } as IMedia);
      return null;
    }
    if (res?.upload_status === UPLOAD_STATUS.ERROR) {
      this.onFailUpdate(res.failed_info.detail);
      return null;
    }
    return res;
  };

  async backendCancel(failed_hint?: number, deleteData?: boolean) {
    this.cleanup();
    const shouldDeleteData = deleteData ?? this.userCancelDeleteData;
    const cancelParams: { upload_secret_id: string; failed_hint?: number; delete_data?: boolean } =
      {
        upload_secret_id: this.upload_secret_id,
        failed_hint,
      };
    if (shouldDeleteData) {
      cancelParams.delete_data = true;
    }
    const response: unknown = await this.dataSource.cancel(cancelParams);
    if (response && Array.isArray(response) && response[0]) {
      const record = response[0] as { failed_info: { detail: string } };
      this.onFailUpdate(record.failed_info.detail, record.failed_info);
    }
    return response;
  }

  finish(media: IMedia) {
    this.cleanup(); // 完成时清理资源
    this.media = media;
    this.onProgressUpdate(100);
    this.onSuccessUpdate();
  }

  cancel(options?: { deleteData?: boolean }) {
    this.userCancelDeleteData = options?.deleteData ?? false;
    // 立即停止轮询，避免取消后 timer 继续触发
    this.cleanup();
    if (this.ossUploadDone) {
      return this.backendCancel(undefined, this.userCancelDeleteData);
    }

    // FIXME 无法 then，也获取不到返回信息
    return this.client.abortMultipartUpload(this.cpt.name, this.cpt.uploadId);
  }

  remove() {
    return this.dataSource.remove({ upload_secret_id: this.upload_secret_id });
  }

  // 资源清理方法
  cleanup(): void {
    this.isCancelled = true;
    if (this.timer) {
      window.clearTimeout(this.timer);
      this.timer = 0;
    }
    if (this.retryTimer) {
      window.clearTimeout(this.retryTimer);
      this.retryTimer = 0;
    }
    // 重置轮询状态
    this.pollingStarted = false;
  }

  // 错误处理方法
  handleUploadError(err: unknown): void {
    this.cleanup(); // 确保清理资源

    if (err instanceof TypeError) {
      this.backendCancel(7); // 回调后端失败
      this.onFailUpdate(String(err));
    } else if (this.isAbortError(err)) {
      // abortMultipartUpload 会直接进入这里
      this.backendCancel(undefined, this.userCancelDeleteData); // 用户取消时传 delete_data
    } else if (this.isNetworkError(err)) {
      this.backendCancel(8); // sdk上传失败
    } else {
      this.backendCancel(8); // sdk上传失败
    }
  }

  handlePrepareError(err: unknown): void {
    const error = err as IUploadError;
    this.upload_secret_id = error?.upload_secret_id || '';
    const detail = get(error, 'failed_info.detail', '');
    const message = typeof err === 'string' ? err : detail || 'upload prepare failed';
    this.onFailUpdate(message, error || {});
    this.cleanup(); // 确保清理资源
  }

  isAbortError(err: unknown): boolean {
    return (err as IAbort)?.name === 'cancel';
  }

  isNetworkError(err: unknown): boolean {
    const error = err as { code?: string };
    return error?.code === 'RequestError' || error?.code === 'ConnectionTimeoutError';
  }
}
